import type { ValueOf } from '@travel-planner/shared/types';
import { useSignIn } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, Mail } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Alert, Button, Heading, Input, InputGroup, Text } from '@/shared/ui';
import { goSignIn } from '../lib/redirect-url';
import css from './forgot-password-form.module.scss';

const STEP = {
  Request: 'request',
  Reset: 'reset',
  Success: 'success',
} as const;

type Step = ValueOf<typeof STEP>;

type FieldErrors = {
  email?: string;
  code?: string;
  password?: string;
};

export const ForgotPasswordForm = () => {
  const { signIn, setActive } = useSignIn();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(STEP.Request);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState('');

  const validateRequest = useCallback(() => {
    const errors: FieldErrors = {};
    if (!email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email))
      errors.email = 'Please enter a valid email address';
    return errors;
  }, [email]);

  const validateReset = useCallback(() => {
    const errors: FieldErrors = {};
    if (!code) errors.code = 'Verification code is required';
    if (!password) errors.password = 'Password is required';
    else if (password.length < 8)
      errors.password = 'Password must be at least 8 characters';
    return errors;
  }, [code, password]);

  const handleRequestCode = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      setError('');
      const errors = validateRequest();
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;

      try {
        await signIn?.create({
          strategy: 'reset_password_email_code',
          identifier: email,
        });
        setFieldErrors({});
        setStep(STEP.Reset);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to send reset code'
        );
      }
    },
    [signIn, email, validateRequest]
  );

  const handleResetPassword = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      setError('');
      const errors = validateReset();
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;

      try {
        const result = await signIn?.attemptFirstFactor({
          strategy: 'reset_password_email_code',
          code,
          password,
        });
        if (result?.status === 'complete' && result.createdSessionId) {
          await setActive?.({ session: result.createdSessionId });
          setStep(STEP.Success);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to reset password'
        );
      }
    },
    [signIn, setActive, code, password, validateReset]
  );

  if (step === STEP.Success) {
    return (
      <div className={css.root}>
        <div className={css.header}>
          <Heading as="h1" size="2xl">
            Password Reset
          </Heading>
          <Text as="p" variant="muted">
            Your password has been reset successfully.
          </Text>
        </div>
        <div className={css.actions}>
          <Button
            size="xl"
            type="button"
            variant="dark"
            onClick={() => navigate({ to: '/' })}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className={css.root}
      onSubmit={step === STEP.Request ? handleRequestCode : handleResetPassword}
    >
      <div className={css.header}>
        <Heading as="h1" size="2xl">
          {step === STEP.Request ? 'Forgot Password?' : 'Reset Password'}
        </Heading>
        <Text as="p" variant="muted">
          {step === STEP.Request
            ? "Enter your email and we'll send you a reset code."
            : 'Enter the code from your email and a new password.'}
        </Text>
      </div>

      {step === STEP.Request && (
        <div className={css.fields}>
          <InputGroup
            error={fieldErrors.email}
            htmlFor="reset-email"
            label="Email"
          >
            <Input
              autoComplete="email"
              id="reset-email"
              name="reset-email"
              placeholder="youremail@gmail.com"
              trailingIcon={<Mail size={16} />}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </InputGroup>
        </div>
      )}

      {step === STEP.Reset && (
        <div className={css.fields}>
          <InputGroup
            error={fieldErrors.code}
            htmlFor="reset-code"
            label="Verification Code"
          >
            <Input
              id="reset-code"
              name="reset-code"
              placeholder="Enter code from email"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </InputGroup>
          <InputGroup
            error={fieldErrors.password}
            htmlFor="new-password"
            label="New Password"
          >
            <Input
              id="new-password"
              name="new-password"
              placeholder="Enter new password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </InputGroup>
        </div>
      )}

      <div className={css.actions}>
        <Button size="xl" type="submit" variant="dark">
          {step === STEP.Request ? 'Send Reset Code' : 'Reset Password'}
        </Button>
        <Button
          size="xl"
          type="button"
          variant="light"
          onClick={() => goSignIn(navigate)}
        >
          Back to Sign In
        </Button>
      </div>

      {error && <Alert icon={<AlertCircle size={16} />}>{error}</Alert>}
    </form>
  );
};
